import { Flex, useDisclosure } from "@chakra-ui/react";

import { useCallback, useEffect, useState } from "react";
import { ActOnBehalfOfModal } from "./ActOnBehalfOfModal";
import { TruncatedText } from "../TruncatedText";
import { CloseIcon, PencilIconSM, OrganisationChartIcon } from "@digital-services-gard-as/anchor-icons";
import { getHierarchyQueryOption } from "@/api/queryOptions/usersAPIQueryOptions";
import { useQuery } from "@tanstack/react-query";
import { RelatedCompanyDto, HierarchicalDto } from "@/api/usersApi";
import { Button } from "@digital-services-gard-as/anchor-react";

type ActOnBehalfOfProps = {
    setIsActingOnBehalfOf: (actingOnBehalfOf: RelatedCompanyDto) => void;
    isActingOnBehalfOf?: RelatedCompanyDto;
    setHierarchyDto: (hierarchy: HierarchicalDto | null) => void;
    isOpen: boolean;
    onClose: () => void;
};

const ActOnBehalfOf = ({
    setIsActingOnBehalfOf,
    isActingOnBehalfOf,
    isOpen,
    setHierarchyDto,
    onClose
}: ActOnBehalfOfProps) => {
    const [hierarchy, setHierarchy] = useState<HierarchicalDto | null>(null);

    const { data: branchCompanies, isError, isFetching: isLoading } = useQuery(getHierarchyQueryOption());

    useEffect(() => {
        if (branchCompanies) {
            setHierarchy(branchCompanies);
            setHierarchyDto(branchCompanies);
        }
    }, [branchCompanies, setHierarchyDto]);

    const actOnBehalfOfCompanies = hierarchy
        ? [...(hierarchy.children || []), ...(hierarchy.parent ? [hierarchy.parent] : [])]
        : [];

    return (
        <ActOnBehalfOfModal
            isOpen={isOpen}
            onClose={onClose}
            isLoading={isLoading}
            isError={isError}
            selectedActingOnBehalfOf={isActingOnBehalfOf}
            setActingOnBehalfOf={setIsActingOnBehalfOf}
            companies={actOnBehalfOfCompanies}
        />
    );
};

type ActOnBehalfOfButtonProps = {
    setIsActingOnBehalfOf: (actingOnBehalfOf: RelatedCompanyDto | undefined) => void;
};

export const ActOnBehalfOfButton = ({ setIsActingOnBehalfOf }: ActOnBehalfOfButtonProps) => {
    const [selectedCompany, setSelectedCompany] = useState<RelatedCompanyDto>();
    const [hierarchy, setHierarchy] = useState<HierarchicalDto | null>(null);

    const {
        open: isActOnBehalfOfModalOpen,
        onOpen: onOpenActOnBehalfOfModal,
        onClose: onCloseActOnBehalfOfModal
    } = useDisclosure();

    useEffect(() => {
        //In case the where the user has selected act on behalf of, and session storage is not cleared
        const actOnBehalfOf = globalThis.sessionStorage.getItem("actOnBehalfOf");
        if (actOnBehalfOf !== null && !selectedCompany) {
            setSelectedCompany(JSON.parse(actOnBehalfOf));
        }
    }, [selectedCompany]);

    useEffect(() => {
        if (selectedCompany) {
            setIsActingOnBehalfOf(selectedCompany);
            globalThis.sessionStorage.setItem("actOnBehalfOf", JSON.stringify(selectedCompany));
        }
    }, [selectedCompany, setIsActingOnBehalfOf]);

    const handleClearActingOnBehalfOf = useCallback(() => {
        setSelectedCompany(undefined);
        setIsActingOnBehalfOf(undefined);
        globalThis.sessionStorage.removeItem("actOnBehalfOf");
    }, [setIsActingOnBehalfOf]);

    //should be children and parents
    const actOnBehalfOfCompanies = hierarchy
        ? [...(hierarchy.children || []), ...(hierarchy.parent ? [hierarchy.parent] : [])]
        : [];

    return (
        <>
            <Flex align="center">
                {Boolean(actOnBehalfOfCompanies.length) && hierarchy && !selectedCompany && (
                    <Button
                        id="hierarchyCompany"
                        aria-label={""}
                        variant="tertiary"
                        onClick={onOpenActOnBehalfOfModal}
                        style={{
                            fontSize: "1.5rem"
                        }}
                        icon={<OrganisationChartIcon title="a11y-title" fontSize="1.5em" />}
                    />
                )}
                {selectedCompany && (
                    <>
                        <Button
                            aria-label="Edit selected company"
                            onClick={onOpenActOnBehalfOfModal}
                            variant="tertiary"
                            style={{
                                fontSize: "1rem"
                            }}
                            icon={<PencilIconSM />}
                        />
                        <Button
                            id="hierarchyCompany"
                            aria-label={""}
                            variant="tertiary"
                            onClick={handleClearActingOnBehalfOf}
                            style={{
                                fontSize: "1.5rem"
                            }}
                            icon={<CloseIcon color="red" />}
                        />
                    </>
                )}
            </Flex>
            <ActOnBehalfOf
                setIsActingOnBehalfOf={setSelectedCompany}
                isOpen={isActOnBehalfOfModalOpen}
                onClose={onCloseActOnBehalfOfModal}
                setHierarchyDto={setHierarchy}
                isActingOnBehalfOf={selectedCompany}
            />
        </>
    );
};

type ActOnBehalfOfTextProps = {
    isActingOnBehalfOf: RelatedCompanyDto;
};

export const ActOnBehalfOfText = ({ isActingOnBehalfOf }: ActOnBehalfOfTextProps) => {
    return (
        <Flex
            bg="anchor.brown.50"
            p="0.5rem"
            alignItems="center"
            justifyContent="space-evenly"
            w="15rem"
            h="2rem"
            border="solid 1px"
            borderColor="anchor.brown.200"
            borderRadius="5px"
        >
            <Flex maxW="100%">
                <TruncatedText text={isActingOnBehalfOf?.companyName} />
            </Flex>
        </Flex>
    );
};
